import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookingSection, PlacesLeft } from "@/components/BookingForm";
import { GuideBlock } from "@/components/GuideBlock";
import { ACTIVE_BIRTH } from "@/data/activeBirth";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const BookButton = ({ label, testid }) => (
  <a
    href="#book"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Hero = ({ ab }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="ab-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/couple-birth.jpg" alt="A pregnant woman leaning on a birth ball while her partner supports her" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-6 max-w-xl text-xs tracking-[0.3em] uppercase text-cream/85"
          data-testid="ab-hero-eyebrow"
        >
          {ab.hero.eyebrow}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight"
            data-testid="ab-hero-title"
          >
            {ab.hero.title}
          </motion.h1>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-7 max-w-2xl"
          data-testid="ab-hero-intro"
        >
          {ab.hero.intro.map((p, i) => (
            <p key={i} className={`text-lg leading-relaxed text-cream ${i > 0 ? "mt-4" : ""}`}>{p}</p>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="ab-hero-facts"
        >
          {ab.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span className={f.includes("€") ? "font-medium text-cream" : ""}>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <BookButton label={ab.hero.primaryCta} testid="ab-hero-book" />
          <a href="#next-workshop" data-testid="ab-hero-next"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {ab.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`ab-faq-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
        data-testid={`ab-faq-toggle-${index}`}
      >
        <span className="font-serif text-xl sm:text-2xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-7 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ActiveBirthPage() {
  const { lang } = useLang();
  const ab = ACTIVE_BIRTH[lang] || ACTIVE_BIRTH.en;
  useSeo({ ...ab.seo, image: `/images/active-birth.jpg` });

  return (
    <main data-testid="active-birth-page">
      <Hero ab={ab} />

      <section className="bg-cream py-24 lg:py-32" data-testid="ab-experience">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img loading="lazy" src="/images/active-birth.jpg" alt="Birth preparation materials at Women's Den" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ink">{ab.experience.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{ab.experience.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ab.experience.p2}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-24" data-testid="ab-testimonials">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ab.testimonials.heading}</h2>
            <div className="mt-12 grid gap-10 md:grid-cols-3 text-left">
              {ab.testimonials.items.map((t, i) => (
                <blockquote key={i} data-testid={`ab-testimonial-${i}`}>
                  <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>"</p>
                  <p className="mt-2 font-serif italic text-lg leading-relaxed text-ink/85">{t.text}</p>
                  <footer className="mt-5 text-xs tracking-[0.25em] uppercase text-clay">— {t.name}</footer>
                </blockquote>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ab-meaning">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{ab.meaning.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft drop-cap">{ab.meaning.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ab.meaning.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ab.meaning.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ab-movements">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ab.movements.heading}</h2>
          </Reveal>
          <div className="mt-12">
            {ab.movements.items.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="grid gap-3 border-t border-ink/10 py-8 sm:grid-cols-12" data-testid={`ab-movement-${i}`}>
                  <span className="font-serif italic text-clay text-lg sm:col-span-1">{m.n}</span>
                  <h3 className="font-serif text-2xl text-ink sm:col-span-4">{m.title}</h3>
                  <p className="text-sm leading-relaxed text-soft sm:col-span-7">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 lg:py-32 grain relative" data-testid="ab-partners">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream">{ab.partners.title}</h2>
            <p className="mt-8 text-lg leading-relaxed text-cream/80">{ab.partners.p1}</p>
            <p className="mt-6 text-lg leading-relaxed text-cream/80">{ab.partners.p2}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ab-change-course">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ab.changeCourse.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{ab.changeCourse.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{ab.changeCourse.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ab.changeCourse.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-24" data-testid="ab-for-us">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ab.forUs.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{ab.forUs.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ab.forUs.p2}</p>
          </Reveal>
        </div>
      </section>

      <GuideBlock ids={["preparing-for-birth"]} bg="bg-cream" testid="ab-guide" />

      <section className="bg-cream py-24 lg:py-32" data-testid="ab-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-8">{ab.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {ab.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ab-meet-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, Active Birth facilitator and founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="ab-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ab.irida.heading}</h2>
            {ab.irida.paras.map((p, i) => (
              <p key={i} className={`mt-6 text-base leading-relaxed text-soft ${i === 0 ? "drop-cap" : ""}`}>{p}</p>
            ))}
            <Link to="/about" data-testid="ab-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {ab.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <BookingSection
        offer="active-birth"
        banner={
          <div id="next-workshop" className="mb-10 border-b border-ink/10 pb-8" data-testid="ab-booking-band">
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-5">{ab.next.heading}</p>
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              <p className="font-serif text-2xl sm:text-3xl text-ink" data-testid="ab-next-date">{ab.next.date}</p>
              <div className="text-sm leading-relaxed text-soft">
                <p data-testid="ab-next-venue">{ab.next.venue}</p>
                <p>{ab.next.time} · <span className="text-ink font-medium">{ab.next.price}</span> · <span className="text-ink font-medium" data-testid="ab-next-solo">{ab.next.solo}</span></p>
                <p>{ab.next.note}</p>
              </div>
              <span className="rounded-full border border-sage/40 bg-sage/10 px-4 py-1.5 text-xs tracking-wide text-sage" data-testid="ab-next-spots">
                <PlacesLeft offer="active-birth" dates={[
                  { value: "2026-10-31", short: lang === "nl" ? "31 okt" : "31 Oct" },
                ]} />
              </span>
            </div>
          </div>
        }
        dates={[
          { value: "2026-10-31", label: lang === "nl" ? "zaterdag 31 oktober 2026" : "Saturday 31 October 2026" },
        ]}
        tickets={[
          { value: "couple", label: lang === "nl" ? "Met z'n tweeën — €190 (één ticket dekt jullie allebei)" : "Two of us — €190 (one ticket covers you both)" },
          { value: "solo", label: lang === "nl" ? "Alleen ik — €95" : "Just me — €95" },
        ]}
      />

      <section className="bg-ink py-24 lg:py-36 grain relative" data-testid="ab-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-cream">{ab.final.title}</h2>
            <p className="mt-10 text-xs tracking-[0.3em] uppercase text-sand/80">{ab.final.heading}</p>
            <p className="mt-4 font-serif text-2xl text-cream">{ab.final.line1}</p>
            <p className="mt-2 text-sm text-cream/70">{ab.final.line2}</p>
            <div className="mt-10">
              <BookButton label={ab.final.cta} testid="ab-final-book" />
            </div>
            <Link to="/contact?topic=Active%20Birth%20Prenatal%20Workshop" data-testid="ab-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {ab.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
